/**
 * KeyboardShortcutsModal — Self-managing modal showing all keyboard shortcuts.
 * Listens for the ? key globally and opens/closes automatically.
 * Also exports a trigger button component.
 */

import { useState, useEffect, useCallback } from "react";
import { X, Keyboard } from "lucide-react";

const btnReset = "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

interface ShortcutDef {
  keys: string[];
  label: string;
}

interface ShortcutGroup {
  title: string;
  shortcuts: ShortcutDef[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "\u05E0\u05D9\u05D5\u05D5\u05D8 \u05DB\u05DC\u05DC\u05D9",
    shortcuts: [
      { keys: ["Ctrl", "K"], label: "\u05E4\u05DC\u05D8\u05EA \u05E4\u05E7\u05D5\u05D3\u05D5\u05EA (Command Palette)" },
      { keys: ["Ctrl", "Shift", "K"], label: "\u05D7\u05D9\u05E4\u05D5\u05E9 \u05D2\u05DC\u05D5\u05D1\u05DC\u05D9 \u05D1\u05DB\u05DC \u05D4\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA" },
      { keys: ["Ctrl", "F"], label: "\u05D7\u05D9\u05E4\u05D5\u05E9 \u05D5\u05D4\u05D7\u05DC\u05E4\u05D4 \u05D2\u05DC\u05D5\u05D1\u05DC\u05D9" },
      { keys: ["?"], label: "\u05E7\u05D9\u05E6\u05D5\u05E8\u05D9 \u05DE\u05E7\u05DC\u05D3\u05EA (\u05D4\u05DE\u05E1\u05DA \u05D4\u05D6\u05D4)" },
      { keys: ["Esc"], label: "\u05E1\u05D2\u05D9\u05E8\u05EA \u05DE\u05D5\u05D3\u05DC/\u05E4\u05D0\u05E0\u05DC" },
    ],
  },
  {
    title: "\u05E2\u05E8\u05D9\u05DB\u05EA \u05EA\u05D5\u05DB\u05DF",
    shortcuts: [
      { keys: ["Ctrl", "E"], label: "\u05DB\u05E0\u05D9\u05E1\u05D4/\u05D9\u05E6\u05D9\u05D0\u05D4 \u05DE\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4" },
      { keys: ["Ctrl", "S"], label: "\u05E9\u05DE\u05D9\u05E8\u05D4 \u05D9\u05D3\u05E0\u05D9\u05EA" },
      { keys: ["Ctrl", "Z"], label: "\u05D1\u05D9\u05D8\u05D5\u05DC (Undo)" },
      { keys: ["Ctrl", "Y"], label: "\u05E9\u05D7\u05D6\u05D5\u05E8 (Redo)" },
      { keys: ["Ctrl", "Shift", "Z"], label: "\u05E9\u05D7\u05D6\u05D5\u05E8 (Redo) \u2014 \u05D7\u05DC\u05D5\u05E4\u05D9" },
    ],
  },
  {
    title: "\u05EA\u05E6\u05D5\u05D2\u05D4",
    shortcuts: [
      { keys: ["Ctrl", "P"], label: "\u05EA\u05E6\u05D5\u05D2\u05D4 \u05DE\u05E7\u05D3\u05D9\u05DE\u05D4 (Preview)" },
      { keys: ["Ctrl", "Shift", "R"], label: "\u05EA\u05E6\u05D5\u05D2\u05D4 \u05E8\u05E1\u05E4\u05D5\u05E0\u05E1\u05D9\u05D1\u05D9\u05EA" },
    ],
  },
  {
    title: "\u05D3\u05E9\u05D1\u05D5\u05E8\u05D3",
    shortcuts: [
      { keys: ["Ctrl", "N"], label: "\u05EA\u05D1\u05E0\u05D9\u05EA \u05D7\u05D3\u05E9\u05D4" },
      { keys: ["/"], label: "\u05DE\u05D9\u05E7\u05D5\u05D3 \u05D7\u05D9\u05E4\u05D5\u05E9" },
    ],
  },
];

/* ================================================================
   Self-managing component — listens for ? key
   ================================================================ */

export function KeyboardShortcutsModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Only trigger on ? when not in an input/textarea
      const tag = (e.target as HTMLElement).tagName;
      const isEditable = tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement).isContentEditable;
      if (e.key === "?" && !isEditable && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      role="dialog"
      aria-modal="true"
      aria-label="\u05E7\u05D9\u05E6\u05D5\u05E8\u05D9 \u05DE\u05E7\u05DC\u05D3\u05EA"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        style={{ direction: "rtl" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/80">
          <div className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-blue-500" />
            <h2 className="text-base font-bold text-gray-800">{"\u05E7\u05D9\u05E6\u05D5\u05E8\u05D9 \u05DE\u05E7\u05DC\u05D3\u05EA"}</h2>
          </div>
          <button
            type="button"
            className={`${btnReset} p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100`}
            onClick={() => setOpen(false)}
            aria-label="\u05E1\u05D2\u05D5\u05E8"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{group.title}</h3>
              <div className="space-y-1.5">
                {group.shortcuts.map((sc) => (
                  <div key={sc.label} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">{sc.label}</span>
                    <div className="flex items-center gap-1" style={{ direction: "ltr" }}>
                      {sc.keys.map((k, i) => (
                        <span key={i}>
                          {i > 0 && <span className="text-gray-300 text-xs mx-0.5">+</span>}
                          <kbd className="inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 text-xs font-mono bg-gray-100 border border-gray-200 rounded-md text-gray-600 shadow-sm">
                            {k}
                          </kbd>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50 text-center">
          <p className="text-xs text-gray-400">
            {"\u05DC\u05D7\u05E6\u05D5"} <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px] border border-gray-200">?</kbd> {"\u05DC\u05E4\u05EA\u05D9\u05D7\u05EA \u05DE\u05E1\u05DA \u05D6\u05D4 \u05D1\u05DB\u05DC \u05E8\u05D2\u05E2"}
          </p>
        </div>
      </div>
    </div>
  );
}